import { toast } from 'sonner';
import { useApiQuery } from '@/hooks/useApiQuery';
import { createMutationHook } from '@/hooks/lib/createMutationHook';
import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type {
  BookableService,
  CreateBookableServiceDto,
  UpdateBookableServiceDto,
} from '@/types/bookingConfig';

export function useBookableServices() {
  return useApiQuery<BookableService[]>({
    queryKey: settingsKeys.bookableServices(),
    queryFn: () =>
      apiGet<BookableService[]>(operationsClient, settingsEndpoints.bookableServices()),
    showErrorToast: false,
  });
}

export const useCreateBookableService = createMutationHook<
  BookableService,
  CreateBookableServiceDto
>({
  mutationFn: (dto) =>
    apiPost<BookableService, CreateBookableServiceDto>(
      operationsClient,
      settingsEndpoints.bookableServices(),
      dto,
    ),
  onSuccess: (_data, _variables, queryClient) => {
    void queryClient.invalidateQueries({
      queryKey: settingsKeys.bookableServices(),
    });
    toast.success('Bookable service created');
  },
});

export const useUpdateBookableService = createMutationHook<
  BookableService,
  { id: string; dto: UpdateBookableServiceDto }
>({
  mutationFn: ({ id, dto }) =>
    apiPatch<BookableService, UpdateBookableServiceDto>(
      operationsClient,
      settingsEndpoints.bookableService(id),
      dto,
    ),
  onSuccess: (_data, _variables, queryClient) => {
    void queryClient.invalidateQueries({
      queryKey: settingsKeys.bookableServices(),
    });
    toast.success('Bookable service updated');
  },
});

export const useDeleteBookableService = createMutationHook<void, string>({
  mutationFn: (id) => apiDelete(operationsClient, settingsEndpoints.bookableService(id)),
  onSuccess: (_data, _variables, queryClient) => {
    void queryClient.invalidateQueries({
      queryKey: settingsKeys.bookableServices(),
    });
    toast.success('Bookable service removed');
  },
});
